import React from "react";
import { Dropdown } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HELP_SITE_ORIGIN, openHelpUrl } from "../../core/help-docs";
import { hasLicensedModule, PROGRAM_MODULES } from "../../core/module-license";
import { filtersSearchString, loadStoredFilters } from "../doc/exp/doc-exp-utils";
import { HUBS } from "../hub/hub-config";
import checkSrc from "./assets/check.svg";
import dialogsSrc from "./assets/dialogs.svg";
import helpSrc from "./assets/help.svg";
import keySrc from "./assets/key.svg";
import logoSrc from "./assets/logo.svg";
import logoutSrc from "./assets/logout.svg";
import packageSrc from "./assets/package.svg";
import sprsSrc from "./assets/sprs.svg";
import stethoscopeSrc from "./assets/stethoscope.svg";
import utensilsSrc from "./assets/utensils.svg";
import walletSrc from "./assets/wallet.svg";
import wrenchSrc from "./assets/wrench.svg";
import "./nav-menu.css";

/** Как Delphi GetUserLaw: отсутствующий бит = разрешено. */
const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const pathMatches = (pathname, path) =>
  pathname === path || pathname.startsWith(`${path}/`);

const isPlainLeftClick = (event) =>
  !event ||
  (event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey);

const selectedMenuKey = (pathname, entries) => {
  const match = entries.find(({ path }) => pathMatches(pathname, path));
  return match?.key;
};

const IS_PROD = process.env.NODE_ENV === "production";

const menuItemsForEnv = (items) =>
  IS_PROD ? items.filter((item) => !item.unimplemented) : items;

const NavItemIcon = ({ src }) => (
  <span className="nav-menu__icon">
    <img src={src} alt="" />
  </span>
);

const withFlyoutLabels = (items, selectedKey) =>
  items.map((item) => {
    const { path, unimplemented, ...rest } = item;
    const content = (
      <span className="nav-flyout__row">
        <span>{item.label}</span>
        {selectedKey === item.key && !item.disabled ? (
          <span className="nav-flyout__check">
            <img src={checkSrc} alt="" />
          </span>
        ) : null}
      </span>
    );

    return {
      ...rest,
      label:
        path && !item.disabled ? (
          <Link to={path} className="nav-flyout__link">
            {content}
          </Link>
        ) : (
          content
        )
    };
  });

const FlyoutDropdown = ({ title, selectedKey, items, onClick, disabled, children }) => (
  <Dropdown
    disabled={disabled}
    menu={{
      items: withFlyoutLabels(items, selectedKey),
      selectedKeys: selectedKey ? [selectedKey] : [],
      onClick: (info) => {
        if (!isPlainLeftClick(info.domEvent)) return;
        onClick?.(info);
      }
    }}
    placement="rightTop"
    trigger={["hover"]}
    mouseEnterDelay={0.08}
    mouseLeaveDelay={0.12}
    overlayClassName="nav-flyout-overlay"
    popupRender={(menu) => (
      <div className="nav-flyout">
        <div className="nav-flyout__title">{title}</div>
        {menu}
      </div>
    )}
  >
    {children}
  </Dropdown>
);

const NavMenu = ({ onOpenTab, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const law = useSelector((state) => state.user.session?.law);
  const license = useSelector((state) => state.license.data);
  const unreadQty = useSelector((state) => state.dialogUnread?.qty) || 0;
  const hasNumberModule = hasLicensedModule(license, PROGRAM_MODULES.NUMBER);
  const hasHealthModule = hasLicensedModule(license, PROGRAM_MODULES.HEALTH);
  const hasWarehouseModule = hasLicensedModule(license, PROGRAM_MODULES.WAREHOUSE);
  const hasNutritionModule = hasLicensedModule(license, PROGRAM_MODULES.NUTRITION);
  const canEditSotr = getLawFlag(law, 0) === 1;
  const canOpenText = getLawFlag(law, 1) === 1;
  const canOpenNumber = getLawFlag(law, 4) === 1;
  const canOpenExp = getLawFlag(law, 5) === 1;
  const canOpenBron = getLawFlag(law, 11) === 1;
  const canOpenPlat = getLawFlag(law, 13) === 1;
  const canOpenStatus = getLawFlag(law, 43) === 1;
  const canOpenPred = getLawFlag(law, 12) === 1;
  const canOpenHolidays = getLawFlag(law, 39) === 1;
  const canOpenKassa = getLawFlag(law, 48) === 1;
  const canOpenCards = getLawFlag(law, 46) === 1;
  const canOpenServis = getLawFlag(law, 98) === 1;
  const canOpenMonitor = getLawFlag(law, 67) === 1;
  const canOpenReports = getLawFlag(law, 81) === 1;
  const canOpenSettings = getLawFlag(law, 40) === 1;
  const canOpenDialogs = getLawFlag(law, 110) === 1;
  const canOpenWarehouseDocuments = getLawFlag(law, 90) === 1;
  const canOpenNutritionRegistration = getLawFlag(law, 83) === 1;
  const canOpenNutritionHalls = getLawFlag(law, 84) === 1;
  const homePath = "/";
  const zalPath = "/spr/zal";
  const tovPath = "/spr/tov";
  const komplPath = "/spr/kompl";
  const sotrPath = "/spr/sotr";
  const templateNaznPath = "/spr/template-nazn";
  const textPath = "/spr/text";
  const holidaysPath = "/spr/holidays";
  const kassaPath = "/spr/kassa";
  const servisPath = "/spr/servis";
  const numberPath = "/spr/number";
  const numbersHubPath = HUBS.numbers.path;
  const dialogsHubPath = HUBS.dialogs.path;
  const warehouseHubPath = HUBS.warehouse.path;
  const nutritionHubPath = HUBS.nutrition.path;
  const healthHubPath = HUBS.health.path;
  const serviceHubPath = HUBS.service.path;
  const sprsHubPath = HUBS.sprs.path;
  const helpHubPath = HUBS.help.path;
  const numberCatPath = "/spr/number-cat";
  const numberSprPath = "/spr/number-spr";
  const numberPricePath = "/number/price";
  const docExpPath = "/doc/exp";
  const bronPath = "/bron";
  const docPlatPath = "/doc/plat";
  const statusPath = "/spr/status";
  const cardsPath = "/spr/cards";
  const cardVidsPath = "/spr/card-vids";
  const predPath = "/spr/pred";
  const clientsPath = "/health/clients";
  const checkDbPath = "/service/check-db";
  const monitorPath = "/service/monitor";
  const reportsPath = "/service/reports";
  const settingsPath = "/service/settings";
  const aboutPath = "/help/about";
  const dialogsPath = "/dialogs";
  const dialogsChannelsPath = "/dialogs/channels";
  const dialogsAgentsPath = "/dialogs/agents";
  const warehouseDocumentsPath = "/warehouse/documents";
  const warehouseRegisterPath = "/warehouse/register";
  const warehouseMovementsPath = "/warehouse/movements";
  const warehouseProductCardPath = "/warehouse/product-card";
  const nutritionPaths = {
    templates: "/nutrition/templates",
    map: "/nutrition/map",
    registration: "/nutrition/register",
    halls: "/nutrition/tables",
    cost: "/nutrition/cost"
  };
  const sprActive =
    location.pathname === sprsHubPath ||
    pathMatches(location.pathname, tovPath) ||
    pathMatches(location.pathname, komplPath) ||
    pathMatches(location.pathname, sotrPath) ||
    pathMatches(location.pathname, templateNaznPath) ||
    pathMatches(location.pathname, textPath) ||
    pathMatches(location.pathname, holidaysPath) ||
    pathMatches(location.pathname, kassaPath) ||
    pathMatches(location.pathname, servisPath) ||
    pathMatches(location.pathname, predPath) ||
    pathMatches(location.pathname, numberSprPath) ||
    pathMatches(location.pathname, statusPath) ||
    pathMatches(location.pathname, cardsPath) ||
    pathMatches(location.pathname, cardVidsPath);
  const numbersActive =
    location.pathname === numbersHubPath ||
    pathMatches(location.pathname, numberPath) ||
    pathMatches(location.pathname, numberCatPath) ||
    pathMatches(location.pathname, numberPricePath) ||
    pathMatches(location.pathname, docExpPath) ||
    pathMatches(location.pathname, bronPath);
  const lechActive =
    location.pathname === healthHubPath ||
    pathMatches(location.pathname, zalPath) ||
    pathMatches(location.pathname, clientsPath);
  const servisActive =
    location.pathname === serviceHubPath ||
    pathMatches(location.pathname, checkDbPath) ||
    pathMatches(location.pathname, monitorPath) ||
    pathMatches(location.pathname, reportsPath) ||
    pathMatches(location.pathname, settingsPath);
  const helpActive =
    location.pathname === helpHubPath || pathMatches(location.pathname, aboutPath);
  const warehouseActive =
    location.pathname === warehouseHubPath || location.pathname.startsWith("/warehouse/");
  const dialogsActive =
    location.pathname === dialogsHubPath || location.pathname.startsWith("/dialogs");
  const nutritionActive =
    location.pathname === nutritionHubPath || location.pathname.startsWith("/nutrition/");
  const platActive = pathMatches(location.pathname, docPlatPath);
  const numbersSelected = selectedMenuKey(location.pathname, [
    { key: "hub", path: numbersHubPath },
    { key: "number-cat", path: numberCatPath },
    { key: "number-price", path: numberPricePath },
    { key: "number", path: numberPath },
    { key: "exp", path: docExpPath },
    { key: "bron", path: bronPath }
  ]);
  const sprSelected = selectedMenuKey(location.pathname, [
    { key: "hub", path: sprsHubPath },
    { key: "template-nazn", path: templateNaznPath },
    { key: "sotr", path: sotrPath },
    { key: "tov", path: tovPath },
    { key: "kompl", path: komplPath },
    { key: "pred", path: predPath },
    { key: "text", path: textPath },
    { key: "holidays", path: holidaysPath },
    { key: "kassa", path: kassaPath },
    { key: "servis", path: servisPath },
    { key: "status", path: statusPath },
    { key: "cards", path: cardsPath },
    { key: "card-vids", path: cardVidsPath },
    { key: "number-spr", path: numberSprPath }
  ]);
  const lechSelected = selectedMenuKey(location.pathname, [
    { key: "hub", path: healthHubPath },
    { key: "clients", path: clientsPath },
    { key: "zal", path: zalPath }
  ]);
  const warehouseSelected = selectedMenuKey(location.pathname, [
    { key: "hub", path: warehouseHubPath },
    { key: "documents", path: warehouseDocumentsPath },
    { key: "register", path: warehouseRegisterPath },
    { key: "movements", path: warehouseMovementsPath },
    { key: "product-card", path: warehouseProductCardPath }
  ]);
  const dialogsSelected = selectedMenuKey(location.pathname, [
    { key: "hub", path: dialogsHubPath },
    { key: "channels", path: dialogsChannelsPath },
    { key: "agents", path: dialogsAgentsPath },
    { key: "dialogs", path: dialogsPath }
  ]);
  const nutritionSelected = selectedMenuKey(location.pathname, [
    { key: "hub", path: nutritionHubPath },
    { key: "templates", path: nutritionPaths.templates },
    { key: "map", path: nutritionPaths.map },
    { key: "registration", path: nutritionPaths.registration },
    { key: "halls", path: nutritionPaths.halls },
    { key: "cost", path: nutritionPaths.cost }
  ]);
  const servisSelected = selectedMenuKey(location.pathname, [
    { key: "hub", path: serviceHubPath },
    { key: "check-db", path: checkDbPath },
    { key: "monitor", path: monitorPath },
    { key: "reports", path: reportsPath },
    { key: "settings", path: settingsPath }
  ]);
  const helpSelected = selectedMenuKey(location.pathname, [
    { key: "hub", path: helpHubPath },
    { key: "about", path: aboutPath }
  ]);

  const openZal = () => {
    onOpenTab({ title: "Кабинеты", url: zalPath });
    navigate(zalPath);
  };

  const openTov = () => {
    onOpenTab({ title: "Товары, услуги", url: tovPath });
    navigate(tovPath);
  };

  const openKompl = () => {
    onOpenTab({ title: "Комплекты", url: komplPath });
    navigate(komplPath);
  };

  const openSotr = () => {
    onOpenTab({ title: "Сотрудники", url: sotrPath });
    navigate(sotrPath);
  };

  const openTemplateNazn = () => {
    onOpenTab({ title: "Шаблоны назначений", url: templateNaznPath });
    navigate(templateNaznPath);
  };

  const openText = () => {
    onOpenTab({ title: "Шаблоны", url: textPath });
    navigate(textPath);
  };

  const openHolidays = () => {
    onOpenTab({ title: "Праздники", url: holidaysPath });
    navigate(holidaysPath);
  };

  const openKassa = () => {
    onOpenTab({ title: "Кассы", url: kassaPath });
    navigate(kassaPath);
  };

  const openServis = () => {
    onOpenTab({ title: "Виды диагнозов и записей в дневник", url: servisPath });
    navigate(servisPath);
  };

  const openHub = (hub) => {
    onOpenTab({ title: hub.tabTitle, url: hub.path });
    navigate(hub.path);
  };

  const openNumbersHub = () => openHub(HUBS.numbers);

  const openNumbers = () => {
    onOpenTab({ title: "Номера", url: numberPath });
    navigate(numberPath);
  };

  const openNumberCats = () => {
    onOpenTab({ title: "Категории номеров", url: numberCatPath });
    navigate(numberCatPath);
  };

  const openNumberSprs = () => {
    onOpenTab({ title: "Дополнительные", url: numberSprPath });
    navigate(numberSprPath);
  };

  const openNumberPrices = () => {
    onOpenTab({ title: "Прайс-листы", url: numberPricePath });
    navigate(numberPricePath);
  };

  const openExp = () => {
    const url = `${docExpPath}${filtersSearchString(loadStoredFilters())}`;
    onOpenTab({ title: "Путевки", url: docExpPath });
    navigate(url);
  };

  const openBron = () => {
    onOpenTab({ title: "Шахматка", url: bronPath });
    navigate(bronPath);
  };

  const openPlat = () => {
    onOpenTab({ title: "Платежи", url: docPlatPath });
    navigate(docPlatPath);
  };

  const openStatus = () => {
    onOpenTab({ title: "Статусы", url: statusPath });
    navigate(statusPath);
  };

  const openCards = () => {
    onOpenTab({ title: "Карты", url: cardsPath });
    navigate(cardsPath);
  };

  const openCardVids = () => {
    onOpenTab({ title: "Типы карт", url: cardVidsPath });
    navigate(cardVidsPath);
  };

  const openPred = () => {
    onOpenTab({ title: "Контрагенты", url: predPath });
    navigate(predPath);
  };

  const openClients = () => {
    onOpenTab({ title: "Клиенты", url: clientsPath });
    navigate(clientsPath);
  };

  const openCheckDb = () => {
    onOpenTab({ title: "Проверка базы", url: checkDbPath });
    navigate(checkDbPath);
  };

  const openMonitor = () => {
    onOpenTab({ title: "Монитор", url: monitorPath });
    navigate(monitorPath);
  };

  const openReports = () => {
    onOpenTab({ title: "Отчёты", url: reportsPath });
    navigate(reportsPath);
  };

  const openSettings = () => {
    onOpenTab({ title: "Настройки", url: settingsPath });
    navigate(settingsPath);
  };

  const openAbout = () => {
    onOpenTab({ title: "О программе", url: aboutPath });
    navigate(aboutPath);
  };

  const openWarehouse = (title, url) => {
    onOpenTab({ title, url });
    navigate(url);
  };

  const lechMenuItems = [
    { key: "clients", label: "Клиенты", path: clientsPath },
    { key: "zal", label: "Кабинеты", path: zalPath }
  ];

  const sprMenuItems = [
    { key: "sotr", label: "Сотрудники", path: sotrPath, disabled: !canEditSotr },
    { key: "tov", label: "Товары, услуги", path: tovPath },
    { key: "kompl", label: "Комплекты", path: komplPath },
    { key: "pred", label: "Контрагенты", path: predPath, disabled: !canOpenPred },
    { key: "template-nazn", label: "Шаблоны назначений", path: templateNaznPath },
    { key: "text", label: "Шаблоны", path: textPath, disabled: !canOpenText },
    { key: "holidays", label: "Праздники", path: holidaysPath, disabled: !canOpenHolidays },
    { key: "kassa", label: "Кассы", path: kassaPath, disabled: !canOpenKassa },
    {
      key: "servis",
      label: "Виды диагнозов и записей в дневник",
      path: servisPath,
      disabled: !canOpenServis
    },
    {
      key: "status",
      label: "Статусы",
      path: statusPath,
      disabled: !canOpenStatus || !hasNumberModule
    },
    { key: "cards", label: "Карты", path: cardsPath, disabled: !canOpenCards },
    { key: "card-vids", label: "Типы карт", path: cardVidsPath, disabled: !canOpenCards },
    {
      key: "number-spr",
      label: "Дополнительные",
      path: numberSprPath,
      disabled: !canOpenNumber || !hasNumberModule
    }
  ];

  /**
   * Плоское меню Delphi PMNumber; Номерной фонд разбит на пункты.
   * unimplemented — заглушки до реализации (law: sert=52, card=56, rent=71);
   * на production скрываются.
   */
  const numbersMenuItems = menuItemsForEnv([
    { key: "exp", label: "Путевки", path: docExpPath, disabled: !canOpenExp },
    { key: "number", label: "Номера", path: numberPath, disabled: !canOpenNumber },
    { key: "number-cat", label: "Категории номеров", path: numberCatPath, disabled: !canOpenNumber },
    { key: "number-price", label: "Прайс-листы", path: numberPricePath, disabled: !canOpenNumber },
    { key: "docs", label: "Документы", disabled: true, unimplemented: true },
    { key: "bron", label: "Шахматка", path: bronPath, disabled: !canOpenBron },
    { key: "sert", label: "Сертификаты", disabled: true, unimplemented: true },
    { key: "card-discount", label: "Дисконтные карты", disabled: true, unimplemented: true },
    { key: "rent", label: "Аренда объектов", disabled: true, unimplemented: true },
    { key: "tarif", label: "Шаблоны путевок", disabled: true, unimplemented: true },
    { key: "housemaid", label: "Сервис номеров", disabled: true, unimplemented: true }
  ]);

  const servisMenuItems = menuItemsForEnv([
    { key: "check-db", label: "Проверка базы", path: checkDbPath },
    { key: "reports", label: "Отчёты", path: reportsPath, disabled: !canOpenReports },
    { key: "monitor", label: "Монитор", path: monitorPath, disabled: !canOpenMonitor },
    { key: "backup", label: "Резервная копия", disabled: true, unimplemented: true },
    { key: "settings", label: "Настройки", path: settingsPath, disabled: !canOpenSettings }
  ]);

  const helpMenuItems = [
    { key: "about", label: "О программе", path: aboutPath },
    { key: "docs", label: "База знаний" }
  ];

  const warehouseMenuItems = [
    {
      key: "documents",
      label: "Накладные",
      path: warehouseDocumentsPath,
      disabled: !canOpenWarehouseDocuments
    },
    { key: "register", label: "Остатки", path: warehouseRegisterPath },
    { key: "product-card", label: "Карточка товара", path: warehouseProductCardPath },
    { key: "movements", label: "Движения товаров", path: warehouseMovementsPath }
  ];

  const dialogsMenuItems = [
    { key: "dialogs", label: "Диалоги", path: dialogsPath, disabled: !canOpenDialogs },
    { key: "channels", label: "Каналы", path: dialogsChannelsPath, disabled: !canOpenSettings },
    { key: "agents", label: "Агенты", path: dialogsAgentsPath, disabled: !canOpenSettings }
  ];

  const nutritionMenuItems = [
    { key: "templates", label: "Шаблоны меню", path: nutritionPaths.templates },
    { key: "map", label: "Карта питания", path: nutritionPaths.map },
    {
      key: "registration",
      label: "Регистрация питания",
      path: nutritionPaths.registration,
      disabled: !canOpenNutritionRegistration
    },
    {
      key: "halls",
      label: "Залы и столы",
      path: nutritionPaths.halls,
      disabled: !canOpenNutritionHalls
    },
    { key: "cost", label: "Стоимость питания", path: nutritionPaths.cost }
  ];

  const onLechMenuClick = ({ key }) => {
    if (key === "zal") openZal();
    if (key === "clients") openClients();
  };

  const onSprMenuClick = ({ key }) => {
    if (key === "sotr") openSotr();
    if (key === "tov") openTov();
    if (key === "kompl") openKompl();
    if (key === "pred") openPred();
    if (key === "template-nazn") openTemplateNazn();
    if (key === "text") openText();
    if (key === "holidays") openHolidays();
    if (key === "kassa") openKassa();
    if (key === "servis") openServis();
    if (key === "status") openStatus();
    if (key === "cards") openCards();
    if (key === "card-vids") openCardVids();
    if (key === "number-spr") openNumberSprs();
  };

  const onNumbersMenuClick = ({ key }) => {
    if (key === "exp") openExp();
    if (key === "number") openNumbers();
    if (key === "number-cat") openNumberCats();
    if (key === "number-price") openNumberPrices();
    if (key === "bron") openBron();
  };

  const onServisMenuClick = ({ key }) => {
    if (key === "check-db") openCheckDb();
    if (key === "reports") openReports();
    if (key === "monitor") openMonitor();
    if (key === "settings") openSettings();
  };

  const onHelpMenuClick = ({ key }) => {
    if (key === "about") openAbout();
    if (key === "docs") openHelpUrl(`${HELP_SITE_ORIGIN}/`);
  };

  const onWarehouseMenuClick = ({ key }) => {
    if (key === "documents") openWarehouse("Накладные", warehouseDocumentsPath);
    if (key === "register") openWarehouse("Остатки", warehouseRegisterPath);
    if (key === "product-card") openWarehouse("Карточка товара", warehouseProductCardPath);
    if (key === "movements") openWarehouse("Движения товаров", warehouseMovementsPath);
  };

  const onDialogsMenuClick = ({ key }) => {
    if (key === "dialogs") openWarehouse("Диалоги", dialogsPath);
    if (key === "channels") openWarehouse("Каналы", dialogsChannelsPath);
    if (key === "agents") openWarehouse("Агенты", dialogsAgentsPath);
  };

  const onNutritionMenuClick = ({ key }) => {
    const titles = {
      templates: "Шаблоны меню",
      map: "Карта питания",
      registration: "Регистрация питания",
      halls: "Залы и столы",
      cost: "Стоимость питания"
    };
    const url = nutritionPaths[key];
    if (!url) return;
    onOpenTab({ title: titles[key], url });
    navigate(url);
  };

  const openHome = () => {
    onOpenTab({ title: "Главная", url: homePath });
    navigate(homePath);
  };

  return (
    <nav className="nav-menu" aria-label="Основное меню">
      <Link
        to={homePath}
        className="nav-menu__brand"
        aria-label="Главная"
        onClick={(e) => {
          if (!isPlainLeftClick(e)) return;
          e.preventDefault();
          openHome();
        }}
      >
        <img src={logoSrc} alt="" />
      </Link>
      <div className="nav-menu__main">
        <FlyoutDropdown
          title="Управление номерами"
          selectedKey={numbersSelected}
          items={numbersMenuItems}
          onClick={onNumbersMenuClick}
          disabled={!hasNumberModule}
        >
          <button
            type="button"
            className={`nav-menu__item${numbersActive ? " nav-menu__item--active" : ""}${!hasNumberModule ? " nav-menu__item--licensed-disabled" : ""}`}
            aria-haspopup="menu"
            aria-disabled={!hasNumberModule}
            disabled={!hasNumberModule}
            onClick={(e) => {
              if (!hasNumberModule || !isPlainLeftClick(e)) return;
              openNumbersHub();
            }}
          >
            {!hasNumberModule && <LockOutlined className="nav-menu__license-lock" />}
            <NavItemIcon src={keySrc} />
            <span>Номера</span>
          </button>
        </FlyoutDropdown>
        {canOpenPlat ? (
          <Link
            to={docPlatPath}
            className={`nav-menu__item${platActive ? " nav-menu__item--active" : ""}`}
            onClick={(e) => {
              if (!isPlainLeftClick(e)) return;
              e.preventDefault();
              openPlat();
            }}
          >
            <NavItemIcon src={walletSrc} />
            <span>Платежи</span>
          </Link>
        ) : (
          <button
            type="button"
            className="nav-menu__item nav-menu__item--licensed-disabled"
            disabled
          >
            <NavItemIcon src={walletSrc} />
            <span>Платежи</span>
          </button>
        )}
        <FlyoutDropdown
          title="Диалоги"
          selectedKey={dialogsSelected}
          items={dialogsMenuItems}
          onClick={onDialogsMenuClick}
        >
          <button
            type="button"
            className={`nav-menu__item${dialogsActive ? " nav-menu__item--active" : ""}`}
            aria-haspopup="menu"
            onClick={(e) => {
              if (!isPlainLeftClick(e)) return;
              openHub(HUBS.dialogs);
            }}
          >
            <NavItemIcon src={dialogsSrc} />
            <span>Диалоги</span>
            {unreadQty > 0 ? <span className="nav-menu__badge">{unreadQty > 99 ? "99+" : unreadQty}</span> : null}
          </button>
        </FlyoutDropdown>
        <FlyoutDropdown
          title="Склад"
          selectedKey={warehouseSelected}
          items={warehouseMenuItems}
          onClick={onWarehouseMenuClick}
          disabled={!hasWarehouseModule}
        >
          <button
            type="button"
            className={`nav-menu__item${warehouseActive ? " nav-menu__item--active" : ""}${!hasWarehouseModule ? " nav-menu__item--licensed-disabled" : ""}`}
            aria-haspopup="menu"
            aria-disabled={!hasWarehouseModule}
            disabled={!hasWarehouseModule}
            onClick={(e) => {
              if (!hasWarehouseModule || !isPlainLeftClick(e)) return;
              openHub(HUBS.warehouse);
            }}
          >
            {!hasWarehouseModule && <LockOutlined className="nav-menu__license-lock" />}
            <NavItemIcon src={packageSrc} />
            <span>Склад</span>
          </button>
        </FlyoutDropdown>
        <FlyoutDropdown
          title="Питание"
          selectedKey={nutritionSelected}
          items={nutritionMenuItems}
          onClick={onNutritionMenuClick}
          disabled={!hasNutritionModule}
        >
          <button
            type="button"
            className={`nav-menu__item${nutritionActive ? " nav-menu__item--active" : ""}${!hasNutritionModule ? " nav-menu__item--licensed-disabled" : ""}`}
            aria-haspopup="menu"
            aria-disabled={!hasNutritionModule}
            disabled={!hasNutritionModule}
            onClick={(e) => {
              if (!hasNutritionModule || !isPlainLeftClick(e)) return;
              openHub(HUBS.nutrition);
            }}
          >
            {!hasNutritionModule && <LockOutlined className="nav-menu__license-lock" />}
            <NavItemIcon src={utensilsSrc} />
            <span>Питание</span>
          </button>
        </FlyoutDropdown>
        <FlyoutDropdown
          title="Лечение"
          selectedKey={lechSelected}
          items={lechMenuItems}
          onClick={onLechMenuClick}
          disabled={!hasHealthModule}
        >
          <button
            type="button"
            className={`nav-menu__item${lechActive ? " nav-menu__item--active" : ""}${!hasHealthModule ? " nav-menu__item--licensed-disabled" : ""}`}
            aria-haspopup="menu"
            aria-disabled={!hasHealthModule}
            disabled={!hasHealthModule}
            onClick={(e) => {
              if (!hasHealthModule || !isPlainLeftClick(e)) return;
              openHub(HUBS.health);
            }}
          >
            {!hasHealthModule && <LockOutlined className="nav-menu__license-lock" />}
            <NavItemIcon src={stethoscopeSrc} />
            <span>Лечение</span>
          </button>
        </FlyoutDropdown>
        <FlyoutDropdown
          title="Сервис"
          selectedKey={servisSelected}
          items={servisMenuItems}
          onClick={onServisMenuClick}
        >
          <button
            type="button"
            className={`nav-menu__item${servisActive ? " nav-menu__item--active" : ""}`}
            aria-haspopup="menu"
            onClick={(e) => {
              if (!isPlainLeftClick(e)) return;
              openHub(HUBS.service);
            }}
          >
            <NavItemIcon src={wrenchSrc} />
            <span>Сервис</span>
          </button>
        </FlyoutDropdown>
        <FlyoutDropdown
          title="Справочники"
          selectedKey={sprSelected}
          items={sprMenuItems}
          onClick={onSprMenuClick}
        >
          <button
            type="button"
            className={`nav-menu__item${sprActive ? " nav-menu__item--active" : ""}`}
            aria-haspopup="menu"
            onClick={(e) => {
              if (!isPlainLeftClick(e)) return;
              openHub(HUBS.sprs);
            }}
          >
            <NavItemIcon src={sprsSrc} />
            <span>Справочники</span>
          </button>
        </FlyoutDropdown>
        <FlyoutDropdown
          title="Помощь"
          selectedKey={helpSelected}
          items={helpMenuItems}
          onClick={onHelpMenuClick}
        >
          <button
            type="button"
            className={`nav-menu__item${helpActive ? " nav-menu__item--active" : ""}`}
            aria-haspopup="menu"
            onClick={(e) => {
              if (!isPlainLeftClick(e)) return;
              openHub(HUBS.help);
            }}
          >
            <NavItemIcon src={helpSrc} />
            <span>Помощь</span>
          </button>
        </FlyoutDropdown>
      </div>
      <button type="button" className="nav-menu__item nav-menu__item--logout" onClick={onLogout}>
        <NavItemIcon src={logoutSrc} />
        <span>Выход</span>
      </button>
    </nav>
  );
};

export default NavMenu;
